import {
  Alert,
  AlertTitle,
  Box,
  Button,
  CircularProgress,
  Link as MuiLink,
  Paper,
  TextField as MuiTextField,
  Typography,
} from '@mui/material';
import { Link } from '@tanstack/react-router';
import { MdCheck } from '@teamworksdev/icons';
import { useMemo } from 'react';
import type { WorkoutLayer } from '@strength/db-schema';

import { WorkoutTemplateSheet } from '../../library/workout-templates/components/WorkoutTemplateSheet';
import type { WorkoutImportValidationResult } from './useWorkoutImport';
import { stripUnknownMetricRefs, unresolvedMissingMetricNames } from './utils';
import { useOrgScopedMetrics } from './OrgScopedMetricsContext';

export interface ImportResultsViewProps {
  /** The pipeline result — carries the missing/suggested mappings. */
  result: WorkoutImportValidationResult;
  /** The working template layers (suggestions already applied, admin-edited). */
  template: WorkoutLayer[];
  /** Monotonic per-upload id — keys the editor so a new upload always remounts. */
  uploadId: number;
  /** Controlled editable template name (seeded from the pipeline's parsed name). */
  templateName: string;
  onTemplateNameChange: (name: string) => void;
  onLayersChange: (layers: WorkoutLayer[]) => void;
  /** Temp UUIDs of suggested metrics still awaiting Approve/Remove. */
  pendingSuggestedMetricIds: string[];
  onApproveSuggestedMetric: (tempUuid: string) => void;
  onRemoveSuggestedMetric: (tempUuid: string) => void;
  /** Temp UUIDs of missing exercises (no suggestion) awaiting Add/Remove. */
  pendingMissingExerciseIds: string[];
  /** Temp UUIDs whose create-exercise request is in flight. */
  addingExerciseIds: string[];
  onAddMissingExercise: (tempUuid: string) => void;
  onRemoveMissingExercise: (tempUuid: string) => void;
  /** Whether Save is allowed (hook-derived: no unresolved/pending metrics + non-empty name). */
  canSave: boolean;
  onSave: () => void;
  isSaving: boolean;
}

/**
 * Step 3 results view (SC-4427). Embeds the existing `WorkoutTemplateSheet`
 * editor with the parsed template so the admin sees the imported workout laid
 * out in the real editor grid, with AI-suggested matches pre-filled into cells.
 * An editable template name field sits above the editor. Unresolved missing
 * metrics render an error banner with a link to /admin/metrics and disable Save;
 * Save is also disabled when the name is empty/whitespace.
 */
export const ImportResultsView = ({
  result,
  template,
  uploadId,
  templateName,
  onTemplateNameChange,
  onLayersChange,
  pendingSuggestedMetricIds,
  onApproveSuggestedMetric,
  onRemoveSuggestedMetric,
  pendingMissingExerciseIds,
  addingExerciseIds,
  onAddMissingExercise,
  onRemoveMissingExercise,
  canSave,
  onSave,
  isSaving,
}: ImportResultsViewProps) => {
  const unresolvedMetrics = unresolvedMissingMetricNames(result);

  // Guard the embedded sheet: getMetric throws for any id not in the org's
  // loaded metrics (including invented "suggested" ids). Drop those refs once
  // the org's real metrics are known.
  const orgScoped = useOrgScopedMetrics();
  const knownMetricIds = useMemo(
    () => new Set((orgScoped?.metrics ?? []).map((m) => m.id)),
    [orgScoped],
  );
  const safeTemplate = useMemo(
    () => stripUnknownMetricRefs(template, knownMetricIds),
    [template, knownMetricIds],
  );

  // Missing exercises still awaiting Add/Remove, with their parsed names.
  const pendingExerciseSet = new Set(pendingMissingExerciseIds);
  const pendingExercises = Object.entries(result.missingExerciseMapping)
    .filter(([tempUuid]) => pendingExerciseSet.has(tempUuid))
    .map(([tempUuid, missing]) => ({ tempUuid, name: missing.name }));
  const pendingSet = new Set(pendingSuggestedMetricIds);
  // Pending suggestions only — already-reviewed ones drop out of the banner.
  const pendingSuggestions = Object.entries(result.suggestedMetricMapping)
    .filter(([tempUuid]) => pendingSet.has(tempUuid))
    .map(([tempUuid, match]) => ({ tempUuid, ...match }));

  return (
    <Paper className="p-6">
      <Box className="flex flex-col gap-4">
        <Box className="flex items-center justify-between gap-4 flex-wrap">
          {/* Editable template name — seeded from the pipeline's parsed name.
              Empty/whitespace disables Save (hook computes canSave). */}
          <MuiTextField
            label="Template Name"
            value={templateName}
            onChange={(e) => onTemplateNameChange(e.target.value)}
            className="w-full sm:w-80"
          />
          <Button
            variant="contained"
            onClick={onSave}
            disabled={!canSave}
            startIcon={
              isSaving ? (
                <CircularProgress size={16} color="inherit" />
              ) : (
                <MdCheck />
              )
            }
          >
            {isSaving ? 'Saving...' : 'Save'}
          </Button>
        </Box>

        {/* Missing metrics block save (per spec). Unresolved missing metrics →
            error banner + link to the metric admin page. Resolved metrics (with
            an accepted suggestion) are not blocking, so they don't appear. */}
        {unresolvedMetrics.length > 0 && (
          <Alert severity="error" role="alert">
            <AlertTitle>Missing metrics</AlertTitle>
            <Typography variant="body2">
              The following metrics don&apos;t exist in this org and must be
              created before you can save:{' '}
              <strong>{unresolvedMetrics.join(', ')}</strong>
            </Typography>
            <Box className="mt-2">
              <MuiLink
                component={Link}
                to="/admin/metrics"
                aria-label="Go to Metrics admin page"
              >
                Go to Metrics admin page
              </MuiLink>
            </Box>
          </Alert>
        )}

        {/* Suggested metrics pending review (per spec). The AI auto-filled these
            into the editor; the admin must Approve (keep) or Remove (strip) each
            before Save enables. Reviewing them is the gate — Removing does not
            re-block save, it just drops the metric from the saved template. */}
        {pendingSuggestions.length > 0 && (
          <Alert severity="info">
            <AlertTitle>Review suggested metrics</AlertTitle>
            <Typography variant="body2" className="mb-2">
              The following metrics were auto-matched to existing org metrics.
              Review each before saving:
            </Typography>
            <Box className="flex flex-col gap-2">
              {pendingSuggestions.map((match) => (
                <Box
                  key={match.tempUuid}
                  className="flex items-center justify-between gap-4 flex-wrap"
                >
                  <Typography variant="body2">
                    <strong>{match.name}</strong> → {match.suggestedName}
                  </Typography>
                  <Box className="flex gap-2">
                    <Button
                      size="small"
                      variant="outlined"
                      onClick={() => onApproveSuggestedMetric(match.tempUuid)}
                    >
                      Approve
                    </Button>
                    <Button
                      size="small"
                      variant="text"
                      color="error"
                      onClick={() => onRemoveSuggestedMetric(match.tempUuid)}
                    >
                      Remove
                    </Button>
                  </Box>
                </Box>
              ))}
            </Box>
          </Alert>
        )}

        {/* Missing exercises with no suggested match. Each must be handled
            before Save: "Add" creates it in the org now and repoints the
            template at the new real id; "Remove" strips it from the template.
            Suggested-match exercises aren't listed — they already resolve to
            existing org exercises. */}
        {pendingExercises.length > 0 && (
          <Alert severity="warning">
            <AlertTitle>Exercises not in this organization</AlertTitle>
            <Typography variant="body2" className="mb-2">
              These exercises don&apos;t exist yet. Add each to the
              organization&apos;s library, or remove it from the template:
            </Typography>
            <Box className="flex flex-col gap-2">
              {pendingExercises.map((exercise) => (
                <Box
                  key={exercise.tempUuid}
                  className="flex items-center justify-between gap-4 flex-wrap"
                >
                  <Typography variant="body2">
                    <strong>{exercise.name}</strong>
                  </Typography>
                  <Box className="flex gap-2">
                    <Button
                      size="small"
                      variant="outlined"
                      disabled={addingExerciseIds.includes(exercise.tempUuid)}
                      onClick={() => onAddMissingExercise(exercise.tempUuid)}
                      startIcon={
                        addingExerciseIds.includes(exercise.tempUuid) ? (
                          <CircularProgress size={14} color="inherit" />
                        ) : undefined
                      }
                    >
                      {addingExerciseIds.includes(exercise.tempUuid)
                        ? 'Adding...'
                        : 'Add'}
                    </Button>
                    <Button
                      size="small"
                      variant="text"
                      color="error"
                      disabled={addingExerciseIds.includes(exercise.tempUuid)}
                      onClick={() => onRemoveMissingExercise(exercise.tempUuid)}
                    >
                      Remove
                    </Button>
                  </Box>
                </Box>
              ))}
            </Box>
          </Alert>
        )}

        {/* The embedded editor renders the parsed template read-only with
            suggestions pre-applied (the hook applies them before seeding
            `template`). Read-only mode skips the exercise-search/scheme/create
            queries (those are edit-path only), so the only org-scoped render
            queries are metrics + benchmarks — supplied by the
            OrgScopedMetricsProvider wrapping the sheet at the page level.
            `key` (a monotonic per-upload id) forces a remount when a new upload
            replaces the template. */}
        {safeTemplate.length > 0 && (
          <Box key={uploadId} className="flex flex-col">
            {/* The grid renders at full height and the page does all the
                vertical scrolling — no nested scroll region. The Sheet still
                owns horizontal scrolling for wide metric columns, so the page
                never gets a horizontal scrollbar. `overscroll-auto` undoes the
                Sheet's default `overscroll-none`, which would otherwise let the
                grid swallow the wheel (it's a scroll container because of that
                horizontal overflow) and strand the page. */}
            <WorkoutTemplateSheet
              layers={safeTemplate}
              readOnly={true}
              showSetsColumn={true}
              onLayersChange={onLayersChange}
              sheetClassName="overscroll-auto"
            />
          </Box>
        )}
      </Box>
    </Paper>
  );
};
